import { GetAllAppTypesResponse } from "./Responses/GetAllAppTypesResponse";
import { GetAllAppTypesRequest } from "./Requests/GetAllAppTypesRequest";
import { ApplyEligibilityCheckRequest } from "./Requests/ApplyEligibilityCheckRequest";
import { GetKxAccommodationOffersForApplicantRequest } from "./Requests/GetKxAccommodationOffersForApplicantRequest";
import { GetStudentGUIDApplicantTypeIDRequest } from "./Requests/GetStudentGUIDApplicantTypeIDRequest";
import { GetKxAccommodationApplicationForApplicantRequest } from "./Requests/GetKxAccommodationApplicationForApplicantRequest";
import { GetKxAccommodationApplicationForApplicantResponse } from "./Responses/GetKxAccommodationApplicationForApplicantResponse";
import { ApplyEligibilityCheckResponse } from "./Responses/ApplyEligibilityCheckResponse";
import { GetKxAccommodationOffersForApplicantResponse } from "./Responses/GetKxAccommodationOffersForApplicantResponse";
import { GetStudentGUIDApplicantTypeIDResponse } from "./Responses/GetStudentGUIDApplicantTypeIDResponse";
import { StudentTimetablePortalCoreDataResponse } from "./Responses/StudentTimetablePortalCoreDataResponse";
import { ConstantImport } from "../Constants/ConstantImport";
import { AcademicYearsResponse } from "./Responses/AcademicYearsResponse";
import { AuthenticationResult, IPublicClientApplication } from "@spt/bezel";
import { SiteResponse } from "./Responses/SiteResponse";
import { SiteWindowResponse } from "./Responses/SiteWindowResponse";

export enum MODE {
    DEVELOPMENT,
    STAGING,
    PRODUCTION,
}

export class AccommodationDataAccess {
    private apiUrl: string = ConstantImport.AccommodationAPI.TargetURL;

    // it's just a one minute buffer but it _vastly_ reduces load time)
    BufferedToken: {
        Timestamp: Date;
        TK: AuthenticationResult | undefined;
    } = {
            Timestamp: new Date(0),
            TK: undefined,
        };

    private msal: IPublicClientApplication;

    constructor(
        _msal_PCA: IPublicClientApplication,
    ) {
        this.msal = _msal_PCA;
        this.apiUrl = ConstantImport.AccommodationAPI.TargetURL;
    }

    // strictly typed data operations
    // make typescript much happier
    FuncBot = {
        GetClosingDateByEventID: {
            Endpoint: "GetClosingDateByEventID/{EventID}",
            Verb: "POST",
            RequestType: String,
            ResponseType: Date,
        },
        GetAllAppTypes: {
            Endpoint: "GetAllAppTypes",
            Verb: "POST",
            RequestType: GetAllAppTypesRequest,
            ResponseType: GetAllAppTypesResponse,
        },
        ApplyEligibilityCheck: {
            Endpoint: "ApplyEligibilityCheck",
            Verb: "POST",
            RequestType: ApplyEligibilityCheckRequest,
            ResponseType: ApplyEligibilityCheckResponse,
        },
        GetKxAccommodationOffersForApplicant: {
            Endpoint: "GetKxAccommodationOffersForApplicant",
            Verb: "POST",
            RequestType: GetKxAccommodationOffersForApplicantRequest,
            ResponseType: GetKxAccommodationOffersForApplicantResponse,
        },
        GetStudentGUIDApplicantTypeID: {
            Endpoint: "GetStudentGUIDApplicantTypeID",
            Verb: "POST",
            RequestType: GetStudentGUIDApplicantTypeIDRequest,
            ResponseType: GetStudentGUIDApplicantTypeIDResponse,
        },
        GetKxAccommodationApplicationForApplicant: {
            Endpoint: "GetKxAccommodationApplicationForApplicant",
            Verb: "POST",
            RequestType: GetKxAccommodationApplicationForApplicantRequest,
            ResponseType: GetKxAccommodationApplicationForApplicantResponse,
        },
        StudentTimetablePortalCoreData: {
            Endpoint: "StudentTimetablePortalCoreData/{Email}",
            Verb: "POST",
            RequestType: String,
            ResponseType: StudentTimetablePortalCoreDataResponse,
        },
        GetKxAcademicYearID: {
            Endpoint: "GetKxAcademicYearID/{AcadYearKXFormat}",
            Verb: "POST",
            RequestType: String,
            ResponseType: Number,
        },
        GetAcademicYears: {
            Endpoint: "GetAcademicYears",
            Verb: "POST",
            RequestType: undefined,
            ResponseType: AcademicYearsResponse,
        },
        CheckSummerBooking: {
            Endpoint: "CheckSummerBooking/{PRID}",
            Verb: "GET",
            RequestType: String,
            ResponseType: Number
        },
        GetSiteByName: {
            Endpoint: "GetSiteByName/{SiteName}",
            Verb: "POST",
            RequestType: String,
            ResponseType: SiteResponse,
        },
        GetActiveSiteWindowsBySiteName: {
            Endpoint: "GetActiveSiteWindowsBySiteName/{SiteName}",
            Verb: "POST",
            RequestType: String,
            ResponseType: Array<SiteWindowResponse>
        },
        GetAdminsBySiteName: {
            Endpoint: "GetAdminsBySiteName/{SiteName}",
            Verb: "POST",
            RequestType: String,
            ResponseType: Array<String>
        }
    };

    public async GetAllAppTypes(
        _prid: string,
        _asrkey: string,
        _currentKxYearID: number,
        _nextKxYearID: number
    ): Promise<GetAllAppTypesResponse> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetAllAppTypes.Endpoint;

        const reqtype = this.FuncBot.GetAllAppTypes.RequestType.prototype;

        let _body: typeof reqtype = {
            PRID: _prid,
            ASRKEY: _asrkey,
            CurrentKxYearID: _currentKxYearID,
            NextKxYearID: _nextKxYearID
        };

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetAllAppTypes.Verb,
                body: JSON.stringify(_body),
            });

            const rsptype = this.FuncBot.GetAllAppTypes.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async ApplyEligibilityCheck(
        _studentID: string,
        _academicYearID: number,
        _applicationTypeID: number
    ): Promise<ApplyEligibilityCheckResponse> {
        // console.log("ApplyEligibilityCheck called...")
        // console.log(_studentID);
        // console.log(_academicYearID);
        // console.log(_applicationTypeID);

        const endpoint = this.apiUrl + "/" + this.FuncBot.ApplyEligibilityCheck.Endpoint;

        const reqtype = this.FuncBot.ApplyEligibilityCheck.RequestType.prototype;

        let _body: typeof reqtype = {
            StudentID: _studentID,
            AcademicYearID: _academicYearID,
            ApplicationTypeID: _applicationTypeID
        };

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.ApplyEligibilityCheck.Verb,
                body: JSON.stringify(_body),
            });

            const rsptype = this.FuncBot.ApplyEligibilityCheck.ResponseType.prototype;
            if (response.ok) {
                var _response: typeof rsptype = await response.json();
                return _response;
            } else {
                return await new Promise((_, reject) => {
                    reject(response.statusText);
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetAcademicYears(): Promise<AcademicYearsResponse> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetAcademicYears.Endpoint;

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetAcademicYears.Verb,
            });

            const rsptype = this.FuncBot.GetAcademicYears.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetKxAcademicYearID(AcadYearKXFormat: string): Promise<number> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetKxAcademicYearID.Endpoint.replace("{AcadYearKXFormat}", AcadYearKXFormat);

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetKxAcademicYearID.Verb,
            });

            const rsptype = this.FuncBot.GetKxAcademicYearID.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response as number;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetClosingDateByEventID(
        EventID: string,
    ): Promise<Date> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetClosingDateByEventID.Endpoint.replace("{EventID}", EventID);

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetClosingDateByEventID.Verb,
            });

            const rsptype = this.FuncBot.GetClosingDateByEventID.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetStudentTimetablePortalCoreData(
        Email: string,
    ): Promise<StudentTimetablePortalCoreDataResponse | undefined> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.StudentTimetablePortalCoreData.Endpoint.replace("{Email}", Email);

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.StudentTimetablePortalCoreData.Verb,
            });

            if (response.status == 200) {
                const rsptype = this.FuncBot.StudentTimetablePortalCoreData.ResponseType.prototype;
                var _response: typeof rsptype = await response.json();
                return _response;
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetKxAccommodationOffersForApplicant(
        _studentID: string,
        _academicYearID: number,
        _applicationTypeID: number
    ): Promise<GetKxAccommodationOffersForApplicantResponse> {

        const endpoint = this.apiUrl + "/" + this.FuncBot.GetKxAccommodationOffersForApplicant.Endpoint;

        const reqtype = this.FuncBot.GetKxAccommodationOffersForApplicant.RequestType.prototype;

        let _body: typeof reqtype = {
            StudentID: _studentID,
            AcademicYearID: _academicYearID,
            ApplicationTypeID: _applicationTypeID
        };

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetKxAccommodationOffersForApplicant.Verb,
                body: JSON.stringify(_body),
            });

            const rsptype = this.FuncBot.GetKxAccommodationOffersForApplicant.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetStudentGUIDApplicantTypeID(
        _studentID: string,
        _academicYearID: number
    ):
        Promise<GetStudentGUIDApplicantTypeIDResponse> {

        const endpoint = this.apiUrl + "/" + this.FuncBot.GetStudentGUIDApplicantTypeID.Endpoint;

        const reqtype = this.FuncBot.GetStudentGUIDApplicantTypeID.RequestType.prototype;

        let _body: typeof reqtype = {
            StudentID: _studentID,
            AcademicYearID: _academicYearID
        };

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetStudentGUIDApplicantTypeID.Verb,
                body: JSON.stringify(_body),
            });

            const rsptype = this.FuncBot.GetStudentGUIDApplicantTypeID.ResponseType.prototype;
            var _response: typeof rsptype = await response.json();
            return _response;
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetKxAccommodationApplicationForApplicant(
        _studentID: string,
        _academicYearID: number,
        _applicationTypeID: number
    ):
        Promise<GetKxAccommodationApplicationForApplicantResponse | undefined> {

        const endpoint = this.apiUrl + "/" + this.FuncBot.GetKxAccommodationApplicationForApplicant.Endpoint;

        const reqtype = this.FuncBot.GetKxAccommodationApplicationForApplicant.RequestType.prototype;

        let _body: typeof reqtype = {
            StudentID: _studentID,
            AcademicYearID: _academicYearID,
            ApplicationTypeID: _applicationTypeID
        };

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetKxAccommodationApplicationForApplicant.Verb,
                body: JSON.stringify(_body),
            });

            if (response.status === 200) {
                const rsptype = this.FuncBot.GetKxAccommodationApplicationForApplicant.ResponseType.prototype;
                var _response: typeof rsptype = await response.json();
                return _response;
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async CheckSummerBooking(
        PRID: string,
    ): Promise<number | undefined> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.CheckSummerBooking.Endpoint.replace("{PRID}", PRID);

        try {
            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.CheckSummerBooking.Verb,
            });

            if (response.status == 200) {
                return Number.parseInt(await response.json());
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetSiteByName(SiteName: string): Promise<SiteResponse | undefined> {

        const endpoint = this.apiUrl + "/" + this.FuncBot.GetSiteByName.Endpoint.replace("{SiteName}", SiteName);

        try {

            let response = await fetch(endpoint, {
                ...(await this.BaseReticuleBody()),
                method: this.FuncBot.GetSiteByName.Verb,
            });
            if (response.status == 200) {
                const rsptype = this.FuncBot.GetSiteByName.ResponseType.prototype;

                var _response: typeof rsptype = await response.json();

                return _response;
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }

        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetActiveSiteWindowsBySiteName(
        SiteName: string,
    ): Promise<Array<SiteWindowResponse> | undefined> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetActiveSiteWindowsBySiteName.Endpoint.replace("{SiteName}", SiteName);

        const baseBody = await this.BaseReticuleBody();

        // console.log(baseBody);

        try {
            let response = await fetch(endpoint, {
                ...baseBody,
                method: this.FuncBot.GetActiveSiteWindowsBySiteName.Verb,
            });

            if (response.status == 200) {
                const rsptype = this.FuncBot.GetActiveSiteWindowsBySiteName.ResponseType.prototype;

                var _response: typeof rsptype = await response.json();

                return _response;
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async GetAdminsBySiteName(
        SiteName: string,
    ): Promise<Array<string> | undefined> {
        const endpoint = this.apiUrl + "/" + this.FuncBot.GetAdminsBySiteName.Endpoint.replace("{SiteName}", SiteName);

        const baseBody = await this.BaseReticuleBody();

        // console.log(baseBody);

        try {
            let response = await fetch(endpoint, {
                ...baseBody,
                method: this.FuncBot.GetAdminsBySiteName.Verb,
            });

            if (response.status == 200) {
                const rsptype = this.FuncBot.GetAdminsBySiteName.ResponseType.prototype;

                var _response: typeof rsptype = await response.json();

                return _response;
            } else if (response.status === 204) {
                return undefined;
            } else {
                return await new Promise((_, reject) => {
                    reject("Failed to communicate with API");
                });
            }
        } catch (error) {
            return await new Promise((_, reject) => {
                reject("Failed to communicate with API");
            });
        }
    }

    public async BaseReticuleBody(): Promise<RequestInit> {
        var now = new Date();
        var onemin = new Date(now);
        onemin.setMinutes(onemin.getMinutes() - 1);

        // console.log("Analysing Buffered Token:");
        // console.log(this.BufferedToken);
        // console.log("Token timestamp:");
        // console.log(this.BufferedToken?.Timestamp);
        // console.log("vs One Minute Ago...");
        // console.log(onemin);

        if (this.BufferedToken === undefined) {
            this.BufferedToken = {
                Timestamp: new Date(0),
                TK: undefined,
            };
        }

        // console.log("ConstantImport.AccommodationAPI.Scope?");
        // console.log(ConstantImport.AccommodationAPI.Scope);

        if (this.BufferedToken.Timestamp < onemin) {
            // console.log("Replenishing stale Buffered Token");
            let _body = {
                account: this.msal.getActiveAccount() ?? undefined,
                scopes: [ConstantImport.AccommodationAPI.Scope],
            };
            // console.log(_body);
            this.BufferedToken.TK = await this.msal.acquireTokenSilent(_body);
            this.BufferedToken.Timestamp = now;
            // console.log(this.BufferedToken);
        } else {
            // console.log("Buffered Token still fresh!");
        }

        return {
            mode: "cors",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + this.BufferedToken.TK!.accessToken,
            },
        };
    }
}